import kebabCase from 'lodash/kebabCase';
import React, {useEffect, useMemo, useState} from 'react';
import type {WithRouterProps} from 'react-router';

import type {CurriculumArea, FilterOption, IndependentPracticeSubject, Subject} from './types';
import {CurriculumAreaDropdown} from './CurriculumAreaDropdown';
import {LoadingSpinner} from '@albert-io/atomic';
import {SubjectCard} from './SubjectCard';
import TabEmptyState from '../TabEmptyState';
import constants from 'client/constants';
import useIndependentPracticeSubjects from './hooks/useIndependentPracticeSubjects';

function mapSubject(subject: IndependentPracticeSubject): Subject {
  const curriculumAreaName = subject.curriculumArea?.name ?? '';
  const curriculumArea: CurriculumArea = {
    id: subject.curriculumArea?.id ?? '',
    name: curriculumAreaName,
    slug: kebabCase(curriculumAreaName)
  };
  return {
    id: subject.id,
    name: subject.name,
    urlSlug: subject.urlSlug,
    logoUrl: subject.logoImageUrl ? `${constants.SUPPLEMENTS_ORIGIN}/${subject.logoImageUrl}` : '',
    isAdaptive: subject.adaptive,
    curriculumArea
  };
}

function extractCurriculumAreas(subjectList: IndependentPracticeSubject[]): CurriculumArea[] {
  const curriculumAreaIds = new Set<string>();
  const uniqueCurriculumAreas: CurriculumArea[] = [];
  subjectList.forEach((s) => {
    const ca = s.curriculumArea;
    if (ca && !curriculumAreaIds.has(ca.id)) {
      curriculumAreaIds.add(ca.id);
      uniqueCurriculumAreas.push({
        id: ca.id,
        name: ca.name,
        slug: kebabCase(ca.name)
      });
    }
  });
  return uniqueCurriculumAreas;
}

export function IndependentPractice({params}: WithRouterProps) {
  'use memo';

  const classroomId: string | undefined = params?.classroomId;
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('All');

  const {
    areIndependentPracticeSubjectsErrored: isError,
    areIndependentPracticeSubjectsLoading: isLoading,
    fetchIndependentPracticeSubjects,
    independentPracticeSubjects: subjects
  } = useIndependentPracticeSubjects();

  useEffect(() => {
    fetchIndependentPracticeSubjects(classroomId);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const curriculumAreas = useMemo(() => extractCurriculumAreas(subjects), [subjects]);

  const filteredSubjects = useMemo(() => {
    const mapped = subjects.map(mapSubject);
    return selectedFilter === 'All'
      ? mapped
      : mapped.filter((s) => s.curriculumArea.name === selectedFilter);
  }, [subjects, selectedFilter]);

  function renderContent() {
    if (isLoading) {
      return <LoadingSpinner bottomMarginLevel={0} size={3} />;
    }

    if (isError) {
      return (
        <TabEmptyState
          copy="We couldn't load your practice subjects."
          subtitle='Something went wrong. Check your connection and try again.'
          icon={['fal', 'circle-exclamation']}
          onRetry={() => fetchIndependentPracticeSubjects(classroomId)}
        />
      );
    }

    if (filteredSubjects.length === 0) {
      return <TabEmptyState copy='No subjects available for practice.' icon={['fal', 'books']} />;
    }

    return (
      <div className='gap-x1 grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))]'>
        {filteredSubjects.map((subject) => (
          <SubjectCard subject={subject} key={subject.id} />
        ))}
      </div>
    );
  }

  return (
    <div className='px-x2 py-x3 m:px-x4 w-full'>
      <div className='mb-x3'>
        <CurriculumAreaDropdown
          curriculumAreas={curriculumAreas}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
        />
      </div>
      {renderContent()}
    </div>
  );
}
